import { createSelector } from "@reduxjs/toolkit";

// ========== Base Selector (robuste) ==========
const selectExtractionState = (state) => state.extraction ?? {};

// ========== Health Check ==========
export const selectHealthStatus = createSelector(
  [selectExtractionState],
  (extraction) => extraction.healthStatus
);

export const selectHealthLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.healthLoading
);

export const selectHealthError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.healthError
);

// ✅ Supporte string ("ok") ou objet ({ mongodb_connected, pipeline_initialized })
export const selectIsHealthy = createSelector(
  [selectHealthStatus],
  (healthStatus) => {
    if (!healthStatus) return false;
    if (typeof healthStatus === "string") {
      return healthStatus.toLowerCase() === "ok";
    }
    return Boolean(
      healthStatus.mongodb_connected && healthStatus.pipeline_initialized
    );
  }
);

// ========== Process Document ==========
export const selectProcessResult = createSelector(
  [selectExtractionState],
  (extraction) => extraction.processResult
);

export const selectProcessLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.processLoading
);

export const selectProcessError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.processError
);

export const selectLastProcessedDocument = createSelector(
  [selectProcessResult],
  (result) => result?.data
);

// ========== Documents List ==========
export const selectDocuments = createSelector(
  [selectExtractionState],
  (extraction) => extraction.documents
);

export const selectDocumentsLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.documentsLoading
);

export const selectDocumentsError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.documentsError
);

export const selectDocumentsCount = createSelector(
  [selectExtractionState],
  (extraction) => extraction.documentsCount
);

export const selectCurrentCollection = createSelector(
  [selectExtractionState],
  (extraction) => extraction.currentCollection
);

// ========== My Documents ==========
export const selectMyDocuments = createSelector(
  [selectExtractionState],
  (extraction) => extraction.myDocuments
);

export const selectMyDocumentsLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.myDocumentsLoading
);

export const selectMyDocumentsError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.myDocumentsError
);

export const selectMyDocumentsTotal = createSelector(
  [selectExtractionState],
  (extraction) => extraction.myDocumentsTotal
);

// ========== Single Document ==========
export const selectCurrentDocument = createSelector(
  [selectExtractionState],
  (extraction) => extraction.currentDocument
);

export const selectDocumentLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.documentLoading
);

export const selectDocumentError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.documentError
);

// ========== Statistics ==========
export const selectStatistics = createSelector(
  [selectExtractionState],
  (extraction) => extraction.statistics
);

export const selectStatisticsLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.statisticsLoading
);

export const selectStatisticsError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.statisticsError
);

export const selectTotalDocuments = createSelector(
  [selectStatistics],
  (statistics) => statistics?.total_documents ?? 0
);

export const selectDocumentsByType = createSelector(
  [selectStatistics],
  (statistics) => statistics?.by_type ?? {}
);

// ========== Delete ==========
export const selectDeleteLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.deleteLoading
);

export const selectDeleteError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.deleteError
);

export const selectDeleteSuccess = createSelector(
  [selectExtractionState],
  (extraction) => extraction.deleteSuccess
);

// ========== Search ==========
export const selectSearchResults = createSelector(
  [selectExtractionState],
  (extraction) => extraction.searchResults
);

export const selectSearchLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.searchLoading
);

export const selectSearchError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.searchError
);

// ========== Combined ==========
export const selectIsProcessing = createSelector(
  [
    selectProcessLoading,
    selectDocumentsLoading,
    selectDocumentLoading,
    selectDeleteLoading,
    selectSearchLoading,
    selectMyDocumentsLoading,
  ],
  (processLoading, documentsLoading, documentLoading, deleteLoading, searchLoading, myDocumentsLoading) =>
    Boolean(processLoading || documentsLoading || documentLoading || deleteLoading || searchLoading || myDocumentsLoading)
);

export const selectHasErrors = createSelector(
  [
    selectHealthError,
    selectProcessError,
    selectDocumentsError,
    selectDocumentError,
    selectDeleteError,
    selectSearchError,
    selectMyDocumentsError,
  ],
  (...errors) => errors.some((e) => e != null)
);

// ========== Filters / Helpers ==========
export const selectDocumentsByDocType = createSelector(
  [selectDocuments, (_, docType) => docType],
  (documents, docType) => (!docType ? documents : documents.filter((d) => d.type_document === docType))
);

export const selectMyDocumentsByType = createSelector(
  [selectMyDocuments, (_, docType) => docType],
  (documents, docType) => (!docType ? documents : documents.filter((d) => d.type_document === docType))
);

export const selectCurrentDocumentParameters = createSelector(
  [selectCurrentDocument],
  (document) => {
    if (!document || document.type_document !== "BILAN_BIOCHIMIQUE") return [];
    return document.parametres ?? [];
  }
);

export const selectAbnormalParameters = createSelector(
  [selectCurrentDocumentParameters],
  (params) => params.filter((p) => p.statut !== "NORMAL")
);

export const selectAvailableCollections = createSelector(
  [selectHealthStatus],
  (healthStatus) => healthStatus?.collections_available ?? []
);

export const selectRecentDocuments = createSelector(
  [selectMyDocuments],
  (docs) => docs.slice(0, 5)
);

export const selectMyDocumentsByCollection = createSelector(
  [selectMyDocuments],
  (docs) => {
    const grouped = {};
    for (const doc of docs) {
      const key = doc.collection ?? doc.type_document ?? "AUTRE";
      (grouped[key] ??= []).push(doc);
    }
    return grouped;
  }
);

export const selectMyDocumentsCountByType = createSelector(
  [selectMyDocuments],
  (docs) => {
    const counts = {};
    for (const d of docs) {
      const t = d.type_document ?? "AUTRE";
      counts[t] = (counts[t] ?? 0) + 1;
    }
    return counts;
  }
);

// ========== Update parameters ==========
export const selectUpdateLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.updateLoading
);

export const selectUpdateError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.updateError
);

export const selectUpdateSuccess = createSelector(
  [selectExtractionState],
  (extraction) => extraction.updateSuccess
);

// ========== Document history ==========
export const selectDocumentHistory = createSelector(
  [selectExtractionState],
  (extraction) => {
    const dh = extraction.documentHistory;
    if (Array.isArray(dh)) return dh;
    if (dh?.history && Array.isArray(dh.history)) return dh.history;
    return [];
  }
);

export const selectDocumentHistoryTotal = createSelector(
  [selectExtractionState, selectDocumentHistory],
  (extraction, historyList) => {
    const dh = extraction.documentHistory;
    if (typeof dh?.total_modifications === "number") return dh.total_modifications;
    return historyList.length;
  }
);

export const selectHistoryLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.historyLoading
);

export const selectHistoryError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.historyError
);

// ========== User Storage ==========
export const selectUserStorage = createSelector(
  [selectExtractionState],
  (extraction) => extraction.userStorage
);

export const selectStorageLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.storageLoading
);

export const selectStorageError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.storageError
);

export const selectStorageUsedMB = createSelector(
  [selectUserStorage],
  (storage) => storage?.used_mb || 0
);

export const selectStorageQuotaMB = createSelector(
  [selectUserStorage],
  (storage) => storage?.quota_mb || 100
);

export const selectStorageAvailableMB = createSelector(
  [selectUserStorage],
  (storage) => storage?.available_mb || 100
);

export const selectStorageDocumentCount = createSelector(
  [selectUserStorage],
  (storage) => storage?.document_count || 0
);

export const selectStorageMaxFiles = createSelector(
  [selectUserStorage],
  (storage) => storage?.max_files || 10
);

export const selectStorageAvailableFiles = createSelector(
  [selectUserStorage, selectStorageDocumentCount, selectStorageMaxFiles],
  (storage, count, maxFiles) =>
    storage?.available_files ?? Math.max(maxFiles - count, 0)
);

export const selectStorageUsagePercent = createSelector(
  [selectStorageDocumentCount, selectStorageMaxFiles],
  (count, maxFiles) =>
    maxFiles > 0 ? Math.min((count / maxFiles) * 100, 100) : 0
);

export const selectIsStorageFull = createSelector(
  [selectStorageAvailableFiles],
  (available) => available <= 0
);

export const selectIsStorageAlmostFull = createSelector(
  [selectStorageUsagePercent],
  (percent) => percent >= 60
);

// ========== Share (single document) ==========
export const selectShareLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.shareLoading ?? false
);

export const selectShareError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.shareError ?? null
);

export const selectShareSuccess = createSelector(
  [selectExtractionState],
  (extraction) => extraction.shareSuccess ?? false
);

export const selectShareResult = createSelector(
  [selectExtractionState],
  (extraction) => extraction.shareResult ?? null
);

// ========== Share (batch / multiple documents) ==========
export const selectBatchShareLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.batchShareLoading ?? false
);

export const selectBatchShareError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.batchShareError ?? null
);

export const selectBatchShareResult = createSelector(
  [selectExtractionState],
  (extraction) => extraction.batchShareResult ?? null
);

// ========== Access list ==========
export const selectAccessList = createSelector(
  [selectExtractionState],
  (extraction) => extraction.accessList ?? []
);

export const selectAccessListLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.accessListLoading ?? false
);

export const selectAccessListError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.accessListError ?? null
);

// ========== Revoke access ==========
export const selectRevokeLoading = createSelector(
  [selectExtractionState],
  (extraction) => extraction.revokeLoading ?? false
);

export const selectRevokeError = createSelector(
  [selectExtractionState],
  (extraction) => extraction.revokeError ?? null
);

export const selectRevokeSuccess = createSelector(
  [selectExtractionState],
  (extraction) => extraction.revokeSuccess ?? false
);

// ========== Combined sharing loading ==========
export const selectIsAnySharePending = createSelector(
  [selectShareLoading, selectBatchShareLoading, selectRevokeLoading, selectAccessListLoading],
  (shareLoading, batchShareLoading, revokeLoading, accessListLoading) =>
    Boolean(shareLoading || batchShareLoading || revokeLoading || accessListLoading)
);

// ========== Derived — doctors with active access ==========
export const selectActiveAccessList = createSelector(
  [selectAccessList],
  (list) =>
    list.filter((entry) => {
      if (!entry.expiresAt) return true; // permanent
      return new Date(entry.expiresAt) > new Date();
    })
);

export const selectExpiredAccessList = createSelector(
  [selectAccessList],
  (list) =>
    list.filter(
      (entry) => entry.expiresAt && new Date(entry.expiresAt) <= new Date()
    )
);

export const selectAccessDoctorCount = createSelector(
  [selectActiveAccessList],
  (list) => list.length
);
